import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api.ts';
import { useMyTenant } from '../hooks/useMyTenant';
import GithubPatOrgForm, { type GithubPatOrgState } from './settings/GithubPatOrgForm';

interface GithubStatus {
  connected: boolean;
  username?: string;
  scopes?: string[];
  connectedAt?: string;
  connectedBy?: string | null;
}

interface Props {
  isAdmin: boolean;
}

export default function GitHubConnectionCard({ isAdmin }: Props) {
  const [status, setStatus] = useState<GithubStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<'connect' | 'disconnect' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokeWarning, setRevokeWarning] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const flashConnected = searchParams.get('github') === 'connected';
  const flashDenied    = searchParams.get('github') === 'denied';

  // ── PAT-fallback flow (issue #785) ──────────────────────────────────────
  // Inline disclosure for users on GitHub Enterprise Server (or any
  // environment where the OAuth flow isn't available) to paste a personal
  // access token. (Historically this section linked out to /settings/byoi,
  // the old GCP-manual BYOI wizard; that wizard was removed in ONB.C3 (A.5)
  // and /settings/byoi now just redirects.) #1541 — the form itself
  // (org input + PAT input + validated save via PATCH /credentials/github,
  // which — as of ONB.C2 — also completes the connect_github setup task) is
  // the shared GithubPatOrgForm, extracted so this card and the onboarding
  // wizard's GitHub step stop diverging on save behaviour.
  const { myTenant } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const [showPatForm, setShowPatForm] = useState(false);

  // ── Default GitHub org (issue: create_new owner resolution) ─────────────────
  // The tenant's deliberate default org for new-repo creation. Persisted to
  // tenant_infrastructure_configs.github_org via the existing infrastructure-config
  // endpoint (NOT a new endpoint). When blank, create_new falls back to the
  // connected login — so individual users "just work" without setting an org.
  const [orgValue, setOrgValue]     = useState('');
  const [orgInitial, setOrgInitial] = useState('');
  const [orgSaving, setOrgSaving]   = useState(false);
  const [orgSaved, setOrgSaved]     = useState(false);
  const [orgError, setOrgError]     = useState<string | null>(null);

  useEffect(() => {
    apiFetch<GithubStatus>('/byoi/github/status')
      .then(setStatus)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Clear the github=connected/denied flag after first render so refresh
  // doesn't keep showing the banner.
  useEffect(() => {
    if (!flashConnected && !flashDenied) return;
    const id = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('github');
      setSearchParams(next, { replace: true });
    }, 4000);
    return () => clearTimeout(id);
  }, [flashConnected, flashDenied, searchParams, setSearchParams]);

  // Load the tenant's current default org once we know the tenant AND GitHub is
  // connected (the input only renders in the connected state). Non-fatal on
  // failure — the field simply starts blank.
  useEffect(() => {
    if (!tenantId || !status?.connected) return;
    let cancelled = false;
    apiFetch<{ githubOrg: string | null }>(`/tenants/${tenantId}/infrastructure-config`)
      .then((cfg) => {
        if (cancelled) return;
        const org = cfg.githubOrg ?? '';
        setOrgValue(org);
        setOrgInitial(org);
      })
      .catch(() => { /* leave blank — org default is optional */ });
    return () => { cancelled = true; };
  }, [tenantId, status?.connected]);

  const handleConnect = async () => {
    setError(null);
    setActing('connect');
    try {
      const res = await apiFetch<{ url: string }>('/byoi/github/connect');
      window.location.href = res.url;
    } catch (e) {
      setError((e as Error).message);
      setActing(null);
    }
  };

  const handleSaveOrg = async () => {
    if (!tenantId || !isAdmin || orgSaving) return;
    const trimmed = orgValue.trim();
    setOrgSaving(true);
    setOrgError(null);
    setOrgSaved(false);
    try {
      // Reuse the existing infrastructure-config endpoint. storageProvider is
      // omitted (preserve-on-absence) so this save never clobbers storage config.
      await apiFetch(`/tenants/${tenantId}/infrastructure-config`, {
        method: 'POST',
        body:   JSON.stringify({ githubOrg: trimmed }),
      });
      setOrgValue(trimmed);
      setOrgInitial(trimmed);
      setOrgSaved(true);
    } catch (e) {
      setOrgError((e as Error).message);
    } finally {
      setOrgSaving(false);
    }
  };

  // Fired by GithubPatOrgForm. Only react to 'token-save' specifically — NOT
  // 'initial' (pre-existing state on mount) and NOT 'org-save' (an org-only
  // edit reports whatever githubTokenSet already was, which can be true from
  // an unrelated pre-existing token; reacting to that would wrongly collapse
  // this disclosure on an org edit that never touched the token). A
  // 'token-save' means the PATCH /credentials/github call actually landed,
  // so refetch status and let the connected-state UI take over, same as the
  // old inline handleSavePat did.
  const handlePatFormSaved = (data: GithubPatOrgState, source: 'initial' | 'org-save' | 'token-save') => {
    if (source !== 'token-save') return;
    setShowPatForm(false);
    apiFetch<GithubStatus>('/byoi/github/status')
      .then(setStatus)
      .catch((e: Error) => setError(e.message));
  };

  const handleCancelPat = () => {
    setShowPatForm(false);
    setError(null);
  };

  const handleDisconnect = async () => {
    if (!confirm('Disconnect GitHub? BluePrint will stop pulling from your repo until reconnected.')) return;
    setError(null);
    setRevokeWarning(null);
    setActing('disconnect');
    try {
      const result = await apiFetch<{ disconnected: boolean; revokedAtProvider: boolean }>(
        '/byoi/github/disconnect', { method: 'DELETE' },
      );
      // Local row was removed but provider revocation failed (e.g. GitHub
      // returned non-2xx). The token is no longer usable from BluePrint, but
      // it remains valid at GitHub until manually revoked or it expires —
      // surface that so the user can revoke it at github.com/settings/applications.
      if (result.disconnected && result.revokedAtProvider === false) {
        setRevokeWarning(
          'GitHub disconnected here, but we could not revoke the token at GitHub. ' +
          'Please revoke it manually at github.com/settings/applications.',
        );
      }
      const fresh = await apiFetch<GithubStatus>('/byoi/github/status');
      setStatus(fresh);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActing(null);
    }
  };

  // Dark-mode regression fix (issue #783) — `--card-bg` was never declared
  // in the design-token system, so `var(--card-bg, white)` hard-fell-through
  // to `white` and broke dark mode. Aligned with the canonical card style
  // used everywhere else in Settings (var(--surface) + var(--radius-lg)).
  const cardStyle: React.CSSProperties = {
    background:    'var(--surface)',
    border:        '1px solid var(--border)',
    borderRadius:  'var(--radius-lg)',
    padding:       20,
    marginBottom:  16,
    boxShadow:     'var(--shadow-sm)',
  };

  // Status banner tokens (issue #783) — replaces hard-coded light hex
  // colours with the design-token + 10%-opacity-rgba pattern used by
  // BillingPage's StatusBadge. Works in both light and dark themes.
  const successBanner: React.CSSProperties = {
    padding: '8px 12px', background: 'rgba(16,185,129,0.1)',
    border: '1px solid var(--success)', borderRadius: 6,
    fontSize: 13, color: 'var(--success)', marginBottom: 12,
  };
  const warningBanner: React.CSSProperties = {
    padding: '8px 12px', background: 'rgba(245,158,11,0.1)',
    border: '1px solid var(--warning)', borderRadius: 6,
    fontSize: 13, color: 'var(--warning)', marginBottom: 12,
  };
  const errorBanner: React.CSSProperties = {
    padding: '8px 12px', background: 'rgba(239,68,68,0.1)',
    border: '1px solid var(--error)', borderRadius: 6,
    fontSize: 13, color: 'var(--error)', marginBottom: 12,
  };

  const primaryBtn: React.CSSProperties = {
    display: 'inline-block',
    padding: '8px 16px',
    fontFamily: 'var(--font-display)',
    fontSize: 13,
    fontWeight: 600,
    color: 'white',
    background: '#1f2328',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
  };

  const ghostBtn: React.CSSProperties = {
    ...primaryBtn,
    background: 'transparent',
    color: 'var(--text)',
    border: '1px solid var(--border)',
  };

  return (
    <div style={cardStyle} data-testid="github-connection-card">
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
        GitHub Connection
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Connect your tenant-wide GitHub account so BluePrint can read repos, create branches, and open PRs on your behalf.
      </p>

      {flashConnected && (
        <div style={successBanner}>
          GitHub connected.
        </div>
      )}
      {flashDenied && (
        <div style={warningBanner}>
          GitHub authorization was cancelled — your account was not connected.
        </div>
      )}

      {loading && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</div>
      )}

      {!loading && error && (
        <div style={errorBanner}>
          {error}
        </div>
      )}

      {!loading && revokeWarning && (
        <div role="alert" style={warningBanner} data-testid="github-revoke-warning">
          {revokeWarning}
        </div>
      )}

      {!loading && status && !status.connected && (
        <div>
          <button
            type="button"
            disabled={!isAdmin || acting === 'connect'}
            onClick={handleConnect}
            style={{ ...primaryBtn, opacity: (!isAdmin || acting === 'connect') ? 0.5 : 1, cursor: (!isAdmin || acting === 'connect') ? 'not-allowed' : 'pointer' }}
            data-testid="github-connect-button"
          >
            {acting === 'connect' ? 'Redirecting…' : 'Connect GitHub'}
          </button>
          {!isAdmin && (
            <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
              Admin role required.
            </span>
          )}
          <div style={{ marginTop: 12 }}>
            {!showPatForm ? (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Using GitHub Enterprise Server, or OAuth not available?{' '}
                <button
                  type="button"
                  onClick={() => { setShowPatForm(true); setError(null); }}
                  disabled={!isAdmin || !tenantId}
                  style={{
                    color: 'var(--primary)',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontFamily: 'var(--font-body)',
                    fontSize: 12,
                    textDecoration: 'underline',
                    cursor: (isAdmin && tenantId) ? 'pointer' : 'not-allowed',
                    opacity: (isAdmin && tenantId) ? 1 : 0.5,
                  }}
                  data-testid="github-show-pat-toggle"
                >
                  Use a personal access token instead
                </button>.
              </div>
            ) : tenantId ? (
              <GithubPatOrgForm
                tenantId={tenantId}
                isAdmin={isAdmin}
                onSaved={handlePatFormSaved}
                onCancel={handleCancelPat}
              />
            ) : null}
          </div>
        </div>
      )}

      {!loading && status?.connected && (
        <div>
          <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>
            Connected as <strong>{status.username ?? 'unknown'}</strong>
            {status.connectedAt && (
              <span style={{ color: 'var(--text-secondary)' }}> · since {new Date(status.connectedAt).toLocaleDateString()}</span>
            )}
          </div>
          {status.scopes && status.scopes.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Scopes: {status.scopes.join(', ')}
            </div>
          )}

          {/* Default-org setting — gives the deliberate org default a home and
              makes the create_new owner-resolution chain visible to admins. */}
          <div
            style={{ marginTop: 4, marginBottom: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}
            data-testid="github-default-org-section"
          >
            <label
              htmlFor="github-default-org-input"
              style={{
                display: 'block', fontFamily: 'var(--font-body)', fontSize: 13,
                fontWeight: 500, color: 'var(--text)', marginBottom: 6,
              }}
            >
              Default GitHub org (optional)
            </label>
            <input
              id="github-default-org-input"
              type="text"
              value={orgValue}
              onChange={(e) => { setOrgValue(e.target.value); setOrgSaved(false); setOrgError(null); }}
              disabled={!isAdmin || orgSaving}
              placeholder="your-org"
              autoComplete="off"
              spellCheck={false}
              aria-describedby="github-default-org-help"
              style={{
                display: 'block', width: '100%', padding: '8px 12px',
                fontFamily: 'var(--font-body)', fontSize: 14,
                color: 'var(--text)', background: 'var(--surface-alt, var(--surface))',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                outline: 'none', boxSizing: 'border-box',
                opacity: (!isAdmin || orgSaving) ? 0.6 : 1,
              }}
              data-testid="github-default-org-input"
            />
            <p
              id="github-default-org-help"
              style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}
            >
              Leave blank to create repos under your connected account
              {' '}(<strong>{status.username ?? 'your account'}</strong>).
            </p>
            {orgError && (
              <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 4 }}>
                {orgError}
              </p>
            )}
            {orgSaved && !orgError && (
              <p
                style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--success)', marginTop: 4 }}
                data-testid="github-default-org-saved"
              >
                Saved.
              </p>
            )}
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                disabled={!isAdmin || orgSaving || orgValue.trim() === orgInitial.trim()}
                onClick={() => void handleSaveOrg()}
                style={{
                  ...ghostBtn,
                  opacity: (!isAdmin || orgSaving || orgValue.trim() === orgInitial.trim()) ? 0.5 : 1,
                  cursor:  (!isAdmin || orgSaving || orgValue.trim() === orgInitial.trim()) ? 'not-allowed' : 'pointer',
                }}
                data-testid="github-default-org-save"
              >
                {orgSaving ? 'Saving…' : 'Save org'}
              </button>
              {!isAdmin && (
                <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                  Admin role required.
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            disabled={!isAdmin || acting === 'disconnect'}
            onClick={handleDisconnect}
            style={{ ...ghostBtn, opacity: (!isAdmin || acting === 'disconnect') ? 0.5 : 1, cursor: (!isAdmin || acting === 'disconnect') ? 'not-allowed' : 'pointer' }}
            data-testid="github-disconnect-button"
          >
            {acting === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </div>
      )}
    </div>
  );
}
